import { useSuspenseQuery } from "@tanstack/react-query";
import { parseISO } from "date-fns";

import { Anchor } from "@/components/Anchor/Anchor";
import { formatDate } from "@/components/FormattedDate/FormattedDate";
import { useSlug } from "@/hooks/useSlug";
import { Card, CardHeader } from "@/modules/analytics/components/Card";
import { DonutChart } from "@/modules/analytics/components/DonutChart";
import { NoData } from "@/modules/analytics/components/NoData";
import { Stat } from "@/modules/analytics/components/Stat";
import { ChartLegend } from "@/modules/analytics/components/utility/ChartLegend";
import { dataColors } from "@/modules/analytics/theme";
import { useAnalyticsQueries } from "@/queries/analytics";
import { routes } from "@/routes";
import { useTranslation } from "@/translations";

const CHART_SIZE = 220;

export function EngagedResidentsCard(): React.ReactNode {
  const slug = useSlug();
  const { t, i18n } = useTranslation();

  const analyticsQueries = useAnalyticsQueries();
  const { data } = useSuspenseQuery(analyticsQueries.getEngagements());

  const engagements = data.engagements;

  const lastMonthEngagement = engagements.find((x) => x.type === "lastMonth");
  const last7DaysEngagement = engagements.find((x) => x.type === "last7Days");
  const last24HoursEngagement = engagements.find((x) => x.type === "last24Hours");

  const noData = !data || !lastMonthEngagement || !last7DaysEngagement || !last24HoursEngagement;

  return (
    <Card>
      <CardHeader
        title={t("component.analytics-widget.engagement.title")}
        tooltip={t("page.analytics-overview.chart.resident-engagement.title-tooltip")}
      >
        <span className="text-caption">
          <Anchor isBold to={routes.analytics.engagementDetails({ slug })}>
            {t("page.analytics-details.link")}
          </Anchor>
        </span>
      </CardHeader>
      {noData ? (
        <NoData />
      ) : (
        <div className="flex flex-col items-center gap-6 @7xl:flex-row @7xl:items-center @7xl:justify-between">
          <div className="flex flex-col items-center gap-4 px-6">
            <DonutChart size={CHART_SIZE} value={lastMonthEngagement?.engagementPercentage ?? 0} />
            <div className="flex flex-col items-center gap-1">
              <ChartLegend
                items={[
                  {
                    label: t("page.analytics-overview.chart.resident-engagement.chart-legend"),
                    color: dataColors.primary,
                  },
                ]}
              />
              {lastMonthEngagement && (
                <span className="text-overline italic text-grey-700">
                  {formatDate(i18n, "dateMonthYearShort", parseISO(lastMonthEngagement.from))} -{" "}
                  {formatDate(i18n, "dateMonthYearShort", parseISO(lastMonthEngagement.to))}
                </span>
              )}
            </div>
          </div>
          <div className="grid w-full grow grid-cols-2 gap-3">
            {last24HoursEngagement && (
              <Stat
                title={t("page.analytics-overview.chart.resident-engagement.card.day")}
                value={`${last24HoursEngagement.engagementPercentage}%`}
              />
            )}
            {last7DaysEngagement && (
              <Stat
                title={t("page.analytics-overview.chart.resident-engagement.card.week")}
                value={`${last7DaysEngagement.engagementPercentage}%`}
              />
            )}
          </div>
        </div>
      )}
    </Card>
  );
}
